import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  API_BASE_URL,
  API_TIMEOUT,
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  ACTIVE_ORG_KEY,
  USER_KEY,
  PERMISSIONS_KEY,
  MENU_ITEMS_KEY,
  LOCKED_MENU_ITEMS_KEY,
  ORGANIZATIONS_KEY,
  AUTH_PERSIST_KEY,
} from '@/lib/utils/constants';
import { storage } from '@/lib/utils/storage';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { mostrarAvisoDeLimite } from '@/lib/api/aviso-de-limite';
import { olvidarEmpresaDemo } from '@/features/mini-prixia/empresa-demo';
import { avisarMisionCumplida } from '@/features/mini-prixia/aviso-de-mision';
import { enSesionDeSoporte, terminarSoporteVencido } from '@/features/auth/soporte-vencido';

/**
 * 429 que NO son el tope de guardados por minuto: los explica la pantalla que los
 * pidió, sin el aviso global de «Reintentar» (W6: los pedidos de tiendas a Prixia).
 */
// Landing nueva (vault 346): el formulario «Pide tu Mini Prixia» explica su propio 429.
const TOPES_QUE_EXPLICA_LA_PANTALLA = ['TOPE_DE_PEDIDOS_DE_TIENDA', 'DEMASIADAS_SOLICITUDES'];
import {
  clavePorPortal,
  leerPorPortal,
  slugDeLaClave,
  olvidarUltimoPortal,
} from '@/lib/utils/portal-storage';

/**
 * Instancia de Axios configurada con interceptores
 */
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Interceptor de request - Inyecta el token JWT
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Por-empresa según la URL del navegador: dos pestañas en dos empresas no
    // se pisan (ver `portal-storage.ts`).
    const token = leerPorPortal<string>(TOKEN_KEY);

    /*
      🔴 Ronda W (W1): un `Authorization` que el pedido trae explícito manda. El canje de
      empresa (`authService.elegirPortal`) manda el token del ingreso; pisarlo con la
      sesión guardada —en una página sin empresa en la URL, la de la ÚLTIMA empresa, de
      otra persona— hacía el canje como esa otra persona: 403 «No tenés acceso».
    */
    if (config.headers?.Authorization) {
      // Un 401 de este pedido no se «arregla» con la sesión guardada: puede ser de otra persona.
      (config as InternalAxiosRequestConfig & { _authExplicito?: boolean })._authExplicito = true;
    } else if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Agregar organización activa al header
    const activeOrgId = leerPorPortal<string>(ACTIVE_ORG_KEY);
    if (activeOrgId && config.headers) {
      config.headers['X-Organization-Id'] = activeOrgId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Interceptor de response - Maneja errores globalmente
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Mini Prixia (Ronda X): el servidor avisa que esta acción cumplió una misión de la demo.
    avisarMisionCumplida(response.headers?.['x-mision-cumplida']);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _authExplicito?: boolean;
    };

    // Si es 401 y no es un retry, intentar refresh token. Un pedido con su propio
    // `Authorization` (el canje con el token del ingreso) no: el 401 vuelve a quien llamó.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._authExplicito
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = leerPorPortal<string>(REFRESH_TOKEN_KEY);

        if (!refreshToken) {
          // No hay refresh token, logout
          handleLogout();
          return Promise.reject(error);
        }

        // Intentar refresh
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
          refreshToken,
        });

        const { token } = response.data.data;

        // El invariante: el token nuevo se escribe donde el interceptor lo
        // va a LEER. `slugDeLaClave(REFRESH_TOKEN_KEY)` devuelve la empresa
        // de la que salió el refresh token de arriba — que en una URL como
        // `/quotes` (un bookmark sin prefijo) NO es la de la URL: la URL
        // dice `quotes` y la sesión vive bajo `refresh_token:datec`. Antes
        // esto usaba `slugEfectivo()` y el token nuevo terminaba en
        // `auth_token:quotes`, una clave que nadie lee.
        storage.set(clavePorPortal(TOKEN_KEY, slugDeLaClave(REFRESH_TOKEN_KEY)), token);

        // Reintentar request original con nuevo token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        /*
          Bordes A12: la sesión de soporte dura 4 h y no se renueva (401 en el refresh). No es
          una sesión vencida cualquiera: se vuelve a la del admin, al panel, y se le dice por qué.
        */
        if (enSesionDeSoporte() && (refreshError as AxiosError)?.response?.status === 401) {
          await terminarSoporteVencido();
          return Promise.reject(refreshError);
        }
        // Refresh falló, logout
        handleLogout();
        return Promise.reject(refreshError);
      }
    }

    // 429 en una escritura (lote D, G2): el tope de guardados por minuto. Un aviso claro con «Reintentar», que
    // repite la misma operación sólo cuando la persona lo toca. No reintenta solo ni en bucle.
    if (
      error.response?.status === 429 &&
      // Ronda W (W6): un 429 con su propio `codigo` de esta lista lo explica la pantalla.
      !TOPES_QUE_EXPLICA_LA_PANTALLA.includes(
        String((error.response?.data as { codigo?: unknown } | undefined)?.codigo ?? '')
      ) &&
      error.config?.method &&
      ['post', 'put', 'patch', 'delete'].includes(error.config.method) &&
      !String(error.config.url ?? '').includes('/auth/')
    ) {
      const pedido = error.config;
      // Quien hizo la llamada espera: si la persona toca «Reintentar» recibe el resultado del reintento (si sale
      // bien la pantalla sigue como si nada; si falla, el error se propaga); si el aviso vence, recibe el 429.
      return new Promise((resolve, reject) => {
        const mostrado = mostrarAvisoDeLimite(
          () => axiosInstance(pedido).then(resolve, reject),
          () => reject(error),
          error.response?.headers?.['retry-after']
        );
        if (!mostrado) reject(error);
      });
    }

    // 404 en operaciones de escritura → dato stale, emitir evento para que la UI refresque
    const status = error.response?.status;
    if (
      status === 404 &&
      error.config?.method &&
      ['put', 'patch', 'delete'].includes(error.config.method)
    ) {
      window.dispatchEvent(
        new CustomEvent('api:stale-resource', {
          detail: {
            method: error.config.method.toUpperCase(),
            url: error.config.url,
            message:
              (error.response?.data as any)?.message || 'El recurso ya no existe o fue modificado.',
          },
        })
      );
    }

    // Auto-reportar errores 5xx al backend (fire and forget)
    if (status && status >= 500) {
      const token = leerPorPortal<string>(TOKEN_KEY);
      if (token) {
        fetch(`${API_BASE_URL}${ENDPOINTS.ERROR_TRACK}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status,
            method: error.config?.method?.toUpperCase() ?? 'UNKNOWN',
            endpoint: error.config?.url ?? 'desconocido',
            pageUrl: window.location.href,
            responseData: error.response?.data,
          }),
        }).catch(() => {}); // nunca lanzar
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Maneja el logout cuando la sesión expira.
 *
 * Borra las claves de la empresa cuya lectura llevó a este 401
 * (`slugDeLaClave(TOKEN_KEY)`, la MISMA resolución que usó el interceptor
 * para armar el header) — no `storage.clear()`. Con el token por-empresa,
 * un `storage.clear()` acá pisaría la sesión de otra pestaña con otra
 * empresa por un 401 que no la afecta a ella.
 *
 * También borra el puntero a la última empresa activa: si no, queda
 * apuntando a una empresa cuyo token se acaba de borrar, y la próxima carga
 * lo sigue probando. Peor: mientras vive, es la precondición del defecto en
 * el que una persona nueva que acepta una invitación aterriza en la sesión
 * de la persona anterior (ver el paso 2 de `slugDeLaClave`).
 *
 * Y la identidad de la persona (`user`, permisos, menú, organizaciones) y el
 * bloque persistido por `zustand` (`AUTH_PERSIST_KEY`): ninguno de los dos es
 * por-empresa, y dejarlos vivos hace que la próxima carga siga mostrando a la
 * persona y `isAuthenticated: true` con un token que ya no existe.
 */
function handleLogout() {
  // Se resuelve ANTES de olvidar el puntero: el puntero es uno de los tres
  // pasos con los que `slugDeLaClave` decide de qué empresa es este token.
  const slug = slugDeLaClave(TOKEN_KEY);
  storage.remove(clavePorPortal(TOKEN_KEY, slug));
  storage.remove(clavePorPortal(REFRESH_TOKEN_KEY, slug));
  storage.remove(clavePorPortal(ACTIVE_ORG_KEY, slug));
  olvidarEmpresaDemo(slug);
  olvidarUltimoPortal();
  storage.remove(USER_KEY);
  storage.remove(PERMISSIONS_KEY);
  storage.remove(MENU_ITEMS_KEY);
  storage.remove(LOCKED_MENU_ITEMS_KEY);
  storage.remove(ORGANIZATIONS_KEY);
  storage.remove(AUTH_PERSIST_KEY);
  window.location.href = '/login';
}

export default axiosInstance;
